<template>
  <ProductQrReader v-if="showProductQrReader" @tag="getTagCode"></ProductQrReader>

  <div v-else class="flex flex-grow flex-col gap-6">
    <template v-if="!exceptionType">
      <template v-if="!nonConformityView">
        <MoProductCard :card-data="moProductCardData" />

        <TextInput
          type="text"
          ref="serialNumberInput"
          class="w-full"
          label="Informe o número de série"
          v-model="serialNumberDisplay"
          theme="secondary"
          :upper-case="true"
          :align="'center'"
          @enter-key-up="blurSerialNumberInput"
          :is-virtual-keyboard-enabled="true"
          :disabled="isSerialNumberInputDisabled"
        />

        <p
          v-if="serialInfoMessage"
          class="-mt-4 animate-pulse text-center text-sm"
          :class="{
            'font-bold text-red-500': serialInfoType === 'error',
            'text-green-600': serialInfoType === 'success',
            'text-gray-500': serialInfoType === 'info'
          }"
        >
          {{ serialInfoMessage }}
        </p>

        <TextInput
          type="text"
          ref="tagCodeInput"
          class="w-full"
          label="Código da TAG"
          :disabled="true"
          v-model="currentTag"
        />

        <div class="flex flex-row gap-2">
          <ActionCard
            class="w-1/2 bg-quaternary py-8 text-dark"
            :icon="IconQrcode"
            text="Carregar produto"
            :callback="onLoadNewProduct"
          />
          <ActionCard
            class="w-1/2 bg-base-light-green py-8 text-white"
            :icon="IconCheckbox"
            ref="attestToConformityBtn"
            text="Apontamento"
            :callback="attestToConformityBtnClick"
            :disabled="isAttestButtonDisabled"
          />
        </div>
        <!-- Não conforme: em manutenção (ver memória do projeto uono-nonconformity-serial). Descomentar quando reativar a feature.
        <VButton
          :disabled="!currentTag || attestToConformityQuery.isFetching.value"
          type="outline"
          color="red"
          title="Não conforme"
          :callback="nonConformity"
        />
        -->
      </template>

      <NonConformityFormView
        v-else
        @clear-non-conformity="onClearNonConformity"
      ></NonConformityFormView>
    </template>

    <ExceptionView v-else :type="exceptionType" @clear-exception="onClearException" />
  </div>

  <DuplicatedFlyerModal
    :is-open="isDuplicatedFlyerModalOpen"
    :first-attestation="duplicatedFlyerFirstAttestation"
    :fallback-message="duplicatedFlyerMessage"
    @confirm="confirmDuplicatedFlyer"
    @cancel="cancelDuplicatedFlyer"
  />
</template>

<script setup lang="ts">
import ActionCard from '@/components/ActionCard/ActionCard.vue'
import type { ActionCardExpose } from '@/components/ActionCard/types'
// import VButton from '@/components/Button/VButton.vue' // usado só pelo botão "Não conforme", comentado enquanto a feature está em manutenção
import DuplicatedFlyerModal, {
  type DuplicatedFlyerFirstAttestation
} from '@/components/DuplicatedFlyerModal/DuplicatedFlyerModal.vue'
import MoProductCard from '@/components/MoProductCard/MoProductCard.vue'
import ProductQrReader from '@/components/ProductQrReader/ProductQrReader.vue'
import TextInput from '@/components/TextInput/TextInput.vue'
import { showDefaultToast } from '@/helpers/toast-models'
import { extractTagFromUrl } from '@/helpers/url-parser'
import { useEventBus } from '@/plugins/eventBus/eventBus'
import type { ApiError } from '@/services/WarrantyApiService/errors'
import { ScanInputType, useAppStore } from '@/stores/app.store'
import { useUserStore } from '@/stores/user.store'
import { IconCheckbox, IconQrcode } from '@tabler/icons-vue'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import ExceptionView from '../Exception/ExceptionView.vue'
import NonConformityFormView from '../NonConformityForm/NonConformityFormView.vue'
import { fetchMoProduct, useAttestToConformity } from './query'

type ExceptionType = 'ended-op' | 'already-attested' | null
type MessageType = 'error' | 'success' | 'info'
type AttestResult = 'success' | 'error' | 'duplicated-flyer'

const appStore = useAppStore()
const userStore = useUserStore()

const isAttestToConformityFetching = ref(false)

const activityId = computed(() => userStore.decodedToken?.activity.id ?? '')

const eventBus = useEventBus()

const emit = defineEmits(['load-new-product'])

const serialNumberInput = ref<HTMLInputElement | null>(null)
const attestToConformityBtn = ref<ActionCardExpose | null>(null)

const serialNumber = ref('')
const serialInfoMessage = ref('')
const serialInfoType = ref<MessageType>('info')

const attestTrigger = ref<'manual' | 'auto-scan-next-tag'>('manual')

// Cartela duplicada: o backend responde TagFromDuplicatedFlyerError (com data e OP do 1º apontamento)
// e o operador confirma no modal para registrar a cópia como não conformidade
const isDuplicatedFlyerModalOpen = ref(false)
const duplicatedFlyerFirstAttestation = ref<DuplicatedFlyerFirstAttestation | null>(null)
const duplicatedFlyerMessage = ref('')
const duplicatedFlyerConfirmed = ref(false)
// Tag lida no auto-scan enquanto o modal estava aberto: carrega depois que o operador decidir
const pendingNextTag = ref<string | null>(null)

const nonConformityView = computed(() => appStore.nonConformityView)

const rawExceptionType = ref<ExceptionType>(null)

const exceptionType = computed(() => rawExceptionType.value)
const canListenToAttest = computed(() => !exceptionType.value)

const isAttestButtonDisabled = computed(
  () => !canAttest.value || attestToConformityQuery.isFetching.value
)

onUnmounted(async () => {
  eventBus.off('submit')
  eventBus.off('attestToConformity')
  eventBus.off('attestToNonConformity')
  eventBus.off('productDetails:loadProduct')
  eventBus.off('productDetails:loadProduct')

  if (!nonConformityView.value) {
    appStore.actions.clearCurrentTag()
    serialNumber.value = ''
    serialInfoMessage.value = ''
  }

  eventBus.emit('clear')
})

onMounted(async () => {
  eventBus.on('productDetails:loadProduct', onLoadNewProduct)

  if (appStore.currentTag) {
    const tag = appStore.currentTag
    appStore.actions.clearCurrentTag()
    await handleTagChange(tag)
  }
})

const isSerialNumberInputDisabled = ref(true)

const currentTag = computed(() => appStore.currentTag || '')
const canAttest = computed(() => {
  const hasTag = !!currentTag.value
  const hasSerialNumber = !!serialNumber.value
  const canGenerateAutomatically =
    !!appStore.currentMoProduct?.autoGeneratedSerialNumber &&
    !appStore.currentMoProduct?.serialNumber

  return hasTag && (hasSerialNumber || canGenerateAutomatically)
})
const manufacturingOrder = computed(() => appStore.currentManufacturingOrder)
const manufacturingOrderId = computed(() => appStore.currentManufacturingOrder?.id)
const showQrCodeReader = computed(
  () => !appStore.currentTag && appStore.scanInputType === ScanInputType.CAMERA
)
const alphaNumericSerialNumber = computed(() => serialNumber.value.replace(/[^a-zA-Z0-9]/g, ''))

const serialNumberDisplay = computed({
  get() {
    const autoGeneratedSerialNumber = appStore.currentMoProduct?.autoGeneratedSerialNumber
    const currentSerialNumber = appStore.currentMoProduct?.serialNumber

    if (!serialNumber.value && autoGeneratedSerialNumber && !currentSerialNumber) {
      return '-'
    }
    return serialNumber.value
  },
  set(value: string) {
    serialNumber.value = value
  }
})

const showProductQrReader = computed(() => showQrCodeReader.value && !exceptionType.value)

const moProductCardData = computed(() =>
  appStore.currentManufacturingOrder
    ? {
        imageUrl: appStore.currentManufacturingOrder.imageUrl,
        productUnitCode: appStore.currentManufacturingOrder.productUnitCode,
        primaryColorHex: appStore.currentManufacturingOrder.primaryColorHex,
        secondaryColorHex: appStore.currentManufacturingOrder.secondaryColorHex,
        description: appStore.currentManufacturingOrder.description,
        size: appStore.currentManufacturingOrder.size,
        year: appStore.currentManufacturingOrder.year,
        checked: appStore.currentManufacturingOrder.checked,
        problem: appStore.currentManufacturingOrder.problem,
        total: appStore.currentManufacturingOrder.total,
        loadedAt: appStore.currentMoProduct?.loadedAt
      }
    : null
)

eventBus.on('submit', async (value) => {
  if (value.startsWith('@@@|')) return

  await handleTagChange(value)
})

async function handleTagChange(value: string) {
  if (!value) return

  if (!appStore.currentTag) {
    await getCurrentMoProduct(value)
  } else if (appStore.currentTag && serialNumber.value) {
    const result = await attestToConformity('auto-scan-next-tag')
    if (result === 'duplicated-flyer') {
      pendingNextTag.value = value
      return
    }
    await getCurrentMoProduct(value)
  } else {
    showDefaultToast('Informe o número de série', 'error', 5000)
    eventBus.emit('clear')
  }
}

async function getTagCode(tag: string) {
  eventBus.emit('submit', tag)
}

const attestToConformityQuery = useAttestToConformity({
  serialNumber: alphaNumericSerialNumber,
  tagCode: currentTag,
  manufacturingOrderId: manufacturingOrder.value?.id!,
  productUnitId: manufacturingOrder.value?.productId!,
  activityId: activityId.value,
  trigger: attestTrigger,
  duplicatedFlyerConfirmed
})

async function blurSerialNumberInput() {
  serialNumber.value = serialNumber.value.replace(/[^a-zA-Z0-9]/g, '')
  serialNumber.value = serialNumber.value.replace(/[^a-zA-Z0-9]/g, '')
  serialNumberInput.value?.blur()
}

async function getCurrentMoProduct(tag: string) {
  attestToConformityBtn.value?.setLoading(true)

  appStore.actions.clearCurrentTag()
  serialNumber.value = ''
  serialInfoMessage.value = ''
  eventBus.emit('clear')

  const filteredTag = extractTagFromUrl(tag)
  appStore.actions.setCurrentTag(filteredTag)
  await nextTick()

  try {
    const response = await fetchMoProduct(currentTag, manufacturingOrderId)

    const successRefetch = !(response instanceof Error)

    if (successRefetch) {
      appStore.actions.setCurrentMoProduct({
        ...response,
        id: response.productUnitId
      })

      await handleProductSerialState(response.serialNumber, response.autoGeneratedSerialNumber)

      attestToConformityBtn.value?.setLoading(false)
      return
    }
  } catch (e) {
    appStore.actions.clearCurrentTag()
    serialNumber.value = ''
    serialInfoMessage.value = ''
  } finally {
    attestToConformityBtn.value?.setLoading(false)
  }
}

async function handleProductSerialState(_serialNumber: string, autoGeneratedSerialNumber: boolean) {
  serialInfoMessage.value = ''
  serialInfoType.value = 'info'

  if (autoGeneratedSerialNumber) {
    serialInfoMessage.value = 'Número de série gerado após o apontamento'
    return
  }

  // Case 1: Tag already has a serial number
  if (_serialNumber) {
    serialNumber.value = _serialNumber
    isSerialNumberInputDisabled.value = true
    serialInfoMessage.value = 'Esta tag já possui número de série'
    return
  }

  // Case 2: No serial and no config (Manual input required)
  isSerialNumberInputDisabled.value = false
  if (serialNumberInput.value) {
    serialNumberInput.value.disabled = false
    await nextTick()
    serialNumberInput.value.focus()
    serialInfoMessage.value = 'Informe o número de série.'
  }
}

async function attestToConformityBtnClick() {
  if (isAttestButtonDisabled.value) {
    showDefaultToast('Informe o número de série e a TAG', 'error', 2000)
    return
  }

  await attestToConformity()

  eventBus.emit('clear')

  if (attestToConformityQuery.isSuccess.value) {
    appStore.actions.clearCurrentTag()
    serialNumber.value = ''
    serialInfoMessage.value = ''
  }
}

async function attestToConformity(
  trigger: 'manual' | 'auto-scan-next-tag' = 'manual'
): Promise<AttestResult> {
  if (isAttestToConformityFetching.value) return 'error'
  isAttestToConformityFetching.value = true
  attestTrigger.value = trigger

  attestToConformityBtn.value?.setLoading(true)

  try {
    await attestToConformityQuery.refetch()

    if (attestToConformityQuery.isError.value) {
      if (attestToConformityQuery.error.value?.name === 'TagFromDuplicatedFlyerError') {
        const params = (attestToConformityQuery.error.value as ApiError).params
        duplicatedFlyerFirstAttestation.value =
          params?.date && params?.orderNumber
            ? { date: String(params.date), orderNumber: params.orderNumber as string | number }
            : null
        duplicatedFlyerMessage.value = attestToConformityQuery.error.value.message
        isDuplicatedFlyerModalOpen.value = true
        return 'duplicated-flyer'
      }

      if (attestToConformityQuery.error.value?.name === 'DuplicatedSerialNumberError') {
        serialNumber.value = ''
        serialInfoMessage.value = ''
        serialNumberInput.value?.focus()
      } else if (
        attestToConformityQuery.error.value?.name !== 'ProductAlreadyAttestedToConformity'
      ) {
        appStore.actions.setCurrentMoProduct(null)
        appStore.actions.clearCurrentTag()
        serialNumber.value = ''
        serialInfoMessage.value = ''
      }
      return 'error'
    }

    const successMessage = duplicatedFlyerConfirmed.value
      ? 'Registrado como cartela duplicada'
      : 'Apontamento realizado com sucesso'
    const successMessageDuration = 2000

    if (attestToConformityQuery.data.value) {
      const response = attestToConformityQuery.data.value

      if (response.isLast) {
        rawExceptionType.value = 'ended-op'
      }
    }
    showDefaultToast(successMessage, 'success', successMessageDuration)
    return 'success'
  } finally {
    attestToConformityBtn.value?.setLoading(false)
    isAttestToConformityFetching.value = false
  }
}

function closeDuplicatedFlyerModal() {
  isDuplicatedFlyerModalOpen.value = false
  duplicatedFlyerFirstAttestation.value = null
  duplicatedFlyerMessage.value = ''
}

async function confirmDuplicatedFlyer() {
  closeDuplicatedFlyerModal()
  duplicatedFlyerConfirmed.value = true

  try {
    const result = await attestToConformity(attestTrigger.value)
    if (result === 'success') {
      appStore.actions.clearCurrentTag()
      serialNumber.value = ''
      serialInfoMessage.value = ''
    }
  } finally {
    duplicatedFlyerConfirmed.value = false
  }

  await loadPendingNextTag()
}

async function cancelDuplicatedFlyer() {
  closeDuplicatedFlyerModal()
  appStore.actions.clearCurrentTag()
  serialNumber.value = ''
  serialInfoMessage.value = ''
  eventBus.emit('clear')

  await loadPendingNextTag()
}

async function loadPendingNextTag() {
  const nextTag = pendingNextTag.value
  pendingNextTag.value = null
  if (nextTag) {
    await getCurrentMoProduct(nextTag)
  }
}

async function onLoadNewProduct() {
  appStore.actions.clearCurrentTag()
  serialNumber.value = ''
  serialInfoMessage.value = ''

  if (appStore.scanInputType === ScanInputType.CAMERA) {
    emit('load-new-product')
  }
}

async function onClearException() {
  rawExceptionType.value = null
  serialNumber.value = ''
  serialInfoMessage.value = ''
}

async function nonConformity() {
  appStore.actions.setNonConformityView(true)
  window.history.pushState({ mo: manufacturingOrder.value?.id, isScanningTag: true }, '', '')
}

async function onClearNonConformity() {
  appStore.actions.setNonConformityView(false)
  serialNumber.value = ''
  serialInfoMessage.value = ''
}

watch(
  () => attestToConformityQuery.error.value,
  (newError) => {
    if (newError) {
      const errorName = newError.name
      const exception =
        ({
          MaxAttestationLimitReachedError: 'ended-op',
          ProductAlreadyAttestedToConformity: 'already-attested'
        }[errorName] as 'ended-op' | 'already-attested') ?? null

      rawExceptionType.value = exception
    } else {
      rawExceptionType.value = null
    }
  }
)

watch(
  canListenToAttest,
  async (newValue) => {
    if (newValue) {
      eventBus.on('attestToConformity', attestToConformityBtnClick)
      eventBus.on('attestToNonConformity', nonConformity)
    } else {
      eventBus.off('attestToConformity')
      eventBus.off('attestToNonConformity')
    }
  },
  { immediate: true }
)
</script>
